import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import RichTextEditor from "@/components/RichTextEditor";
import MultiPhotoUploader from "@/components/MultiPhotoUploader";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const APPROVAL_BODIES = ["AICTE", "PCI", "DTE", "University", "Others"];

const empty = {
  name: "",
  code: "",
  course_id: "",
  course_type: "",
  approval_body: "",
  head_of_department: "",
  head_of_department_id: "",
  about_html: "",
  is_active: true,
  order: 0,
  photos: [],
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

export default function Departments() {
  const { role } = useAuth();
  const writable = role === "institute_admin";
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    const [d, c, s, m] = await Promise.all([
      api.get("/institute/departments"),
      api.get("/institute/courses"),
      api.get("/institute/staff").catch(() => ({ data: [] })),
      api.get("/masters/staff_designation").catch(() => ({ data: [] })),
    ]);
    setItems(d.data);
    setCourses(c.data || []);
    setStaff(s.data || []);
    setDesignations(m.data || []);
  };
  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ ...empty, ...d });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Department name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        code: form.code,
        course_id: form.course_id,
        approval_body: form.approval_body,
        head_of_department_id: form.head_of_department_id,
        about_html: form.about_html || "",
        is_active: form.is_active,
        order: Number(form.order) || 0,
        photos: form.photos || [],
      };
      if (editing) {
        const { data } = await api.put(`/institute/departments/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Department updated");
      } else {
        const { data } = await api.post(`/institute/departments`, payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Department added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const d = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institute/departments/${d.id}`);
      setItems((arr) => arr.filter((x) => x.id !== d.id));
      toast.success("Department removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid="departments-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Master</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Departments</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Manage academic departments — name, code, course type, approval body and head of
            department.
          </p>
        </div>
        <button
          onClick={openCreate}
          disabled={!writable}
          data-testid="department-add-button"
          title={!writable ? "Only Institute Admin can add departments" : undefined}
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={14} strokeWidth={1.5} /> Add Department
        </button>
      </div>

      {!writable && (
        <div
          data-testid="departments-readonly-banner"
          className="mb-6 px-4 py-3 border border-[#d4af37]/40 bg-[#d4af37]/5 rounded-sm text-sm text-[#8a6d1d]"
        >
          You have read-only access. Only an Institute Admin can manage departments.
        </div>
      )}

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : items.length === 0 ? (
        <div
          data-testid="departments-empty"
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
        >
          <GraduationCap size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">No departments yet</div>
          <div className="mt-2 text-sm text-[#6b655b]">
            Add your first department to begin recording intake and fees.
          </div>
          {writable && (
            <button
              onClick={openCreate}
              className="mt-6 inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm"
            >
              <Plus size={14} strokeWidth={1.5} /> Add the first department
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="departments-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {["Order", "Name", "Code", "Course", "Approval", "HoD", "Status", ""].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr
                  key={d.id}
                  data-testid={`department-row-${d.id}`}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                >
                  <td className="px-4 py-4 font-mono text-xs text-[#6b655b] whitespace-nowrap">
                    {d.order ?? 0}
                  </td>
                  <td className="px-4 py-4 text-[#1c1a17]">{d.name}</td>
                  <td className="px-4 py-4 font-mono text-xs text-[#6b655b]">
                    {d.code || "—"}
                  </td>
                  <td className="px-4 py-4 text-[#6b655b]">{d.course_type || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{d.approval_body || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{d.head_of_department || "—"}</td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-block w-2 h-2 rounded-full mr-2 ${
                        d.is_active ? "bg-[#3a5a40]" : "bg-[#9e2a2b]"
                      }`}
                    />
                    <span className="text-xs text-[#6b655b]">
                      {d.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(d)}
                      data-testid={`department-edit-${d.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} /> {writable ? "Edit" : "View"}
                    </button>
                    {writable && (
                      <button
                        onClick={() => setConfirmDelete(d)}
                        data-testid={`department-delete-${d.id}`}
                        className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={14} strokeWidth={1.5} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto"
          data-testid="department-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Department" : "Add Department"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Department Name *">
              <input
                data-testid="dept-name-input"
                className={inputCls}
                value={form.name}
                disabled={!writable}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Department Code">
              <input
                data-testid="dept-code-input"
                className={inputCls}
                value={form.code}
                disabled={!writable}
                onChange={(e) => set("code", e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="Course Type">
              <select
                data-testid="dept-course-type"
                className={inputCls}
                value={form.course_id}
                disabled={!writable}
                onChange={(e) => set("course_id", e.target.value)}
              >
                <option value="">— Select —</option>
                {courses
                  .filter((c) => c.is_active !== false)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.course_type} ({c.mode})
                    </option>
                  ))}
              </select>
              {courses.length === 0 && (
                <div className="text-[11px] text-[#8a6d1d] mt-1">
                  No courses defined yet. Add one under Courses master first.
                </div>
              )}
            </Field>
            <Field label="Approval Body">
              <select
                data-testid="dept-approval-body"
                className={inputCls}
                value={form.approval_body}
                disabled={!writable}
                onChange={(e) => set("approval_body", e.target.value)}
              >
                <option value="">— Select —</option>
                {APPROVAL_BODIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Head of Department" full>
              {(() => {
                // Set of teaching designation names
                const teachingNames = new Set(
                  designations
                    .filter((d) => d.staff_type === "teaching")
                    .map((d) => d.name),
                );
                const eligible = staff.filter(
                  (u) =>
                    u.is_active !== false &&
                    teachingNames.has(u.designation || "") &&
                    (!editing || (u.department_id || "") === (editing.id || "")),
                );
                return (
                  <>
                    <select
                      data-testid="dept-hod-select"
                      className={inputCls}
                      value={form.head_of_department_id}
                      disabled={!writable || !editing}
                      onChange={(e) => set("head_of_department_id", e.target.value)}
                    >
                      <option value="">— Select HoD —</option>
                      {eligible.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name || u.username} {u.designation ? `· ${u.designation}` : ""}
                        </option>
                      ))}
                    </select>
                    <div className="text-[11px] text-[#6b655b] mt-1">
                      {!editing
                        ? "Save the department first, then assign teaching staff to it. After that you can pick the HoD."
                        : eligible.length === 0
                          ? "No teaching staff are currently assigned to this department. Assign staff under Staff & Activity, then return to set HoD."
                          : "Only teaching staff assigned to this department appear here."}
                    </div>
                  </>
                );
              })()}
            </Field>
            <Field label="Status" full>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="dept-active-checkbox"
                  checked={form.is_active}
                  disabled={!writable}
                  onChange={(e) => set("is_active", e.target.checked)}
                  className="w-4 h-4 accent-[#234333]"
                />
                <span className="text-sm text-[#1c1a17]">Department is active</span>
              </label>
            </Field>
            <Field label="ReOrder Level (lower number = appears first on website)">
              <input
                data-testid="dept-order-input"
                type="number"
                min={0}
                max={9999}
                step={1}
                className={inputCls}
                value={form.order ?? 0}
                disabled={!writable}
                onChange={(e) => set("order", e.target.value === "" ? 0 : Number(e.target.value))}
                placeholder="e.g., 10"
              />
            </Field>
            <Field label={`Department Photos (max ${5} · 300 KB each)`}>
              <div className="text-[11px] text-[#6b655b] mb-2">
                Up to 5 photos appear on the public website's department page.
              </div>
              {writable ? (
                <MultiPhotoUploader
                  testid="dept-photos"
                  kind="inst_dept_photo"
                  photos={form.photos || []}
                  onChange={(next) => set("photos", next)}
                  max={5}
                  maxBytes={300 * 1024}
                  humanMax="300 KB"
                />
              ) : (form.photos || []).length === 0 ? (
                <div className="text-sm text-[#6b655b] italic">No photos uploaded.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                  {form.photos.map((p, i) => (
                    <img
                      key={p.public_id || i}
                      src={p.url}
                      alt={`dept-${i}`}
                      className="w-full aspect-square object-cover rounded-sm border border-[#e5e0d8]"
                    />
                  ))}
                </div>
              )}
            </Field>
            <Field label="About / Description" full>
              {writable ? (
                <>
                  <RichTextEditor
                    testid="dept-about-editor"
                    value={form.about_html || ""}
                    onChange={(html) => set("about_html", html)}
                    placeholder="Describe the department — vision, programs offered, faculty strength, labs, accreditations."
                  />
                  <div className="mt-1 text-[11px] text-[#6b655b]">
                    Supports formatting, lists, headings, links and text alignment. Script tags
                    and unsafe HTML are stripped automatically.
                  </div>
                </>
              ) : (
                <div
                  data-testid="dept-about-readonly"
                  className="prose prose-sm max-w-none border border-[#e5e0d8] rounded-sm bg-[#f9f8f6] px-4 py-3 min-h-[80px] text-[#1c1a17]"
                  dangerouslySetInnerHTML={{
                    __html:
                      form.about_html ||
                      '<p class="text-[#6b655b] italic">No description provided.</p>',
                  }}
                />
              )}
            </Field>
          </div>
          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="dept-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            {writable && (
              <button
                onClick={save}
                disabled={saving}
                data-testid="dept-save-button"
                className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
              >
                {saving ? "Saving…" : editing ? "Update" : "Create"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove this department?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.name} will be removed. Linked intake & fees records remain but will
              show the department as missing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="dept-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
